import React, { useState } from "react";

import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, IconButton, Stack, TableRow, Typography } from "@mui/material";
import { FormatDateUtil, WideRow } from "store/types";

import { BodyCell } from "./styles";

interface Props {
  row: WideRow;
  measureColumns: string[];
  visibleMeasures: string[];
  depth: number;
  formatDate: FormatDateUtil;
  onEditRow: (row: WideRow) => void;
  onEditCell: (row: WideRow, measure: string) => void;
  onDeleteRow: (row: WideRow) => void;
}

export const WideTableRow: React.FC<Props> = ({
  row,
  measureColumns,
  visibleMeasures,
  depth,
  formatDate,
  onEditRow,
  onEditCell,
  onDeleteRow
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = row.children.length > 0;
  const pl = depth * 20;

  const shownMeasures =
    visibleMeasures.length === 0
      ? measureColumns
      : measureColumns.filter((m) => !visibleMeasures.includes(m));

  return (
    <>
      <TableRow
        hover
        sx={{
          "& td": { fontWeight: !depth ? 500 : 400, bgcolor: depth ? "rgba(246,248,252,0.5)" : "white" },
          "& .row-actions": { opacity: 0, transition: "opacity 0.15s" },
          "&:hover .row-actions": { opacity: 1 },
          "& .cell-pencil": { opacity: 0, transition: "opacity 0.15s" },
          "&:hover .cell-pencil": { opacity: 1 }
        }}
      >
        {/* Date */}
        <BodyCell>
          <Stack direction="row" alignItems="center" sx={{ pl: `${pl}px` }}>
            {hasChildren ? (
              <Box
                component="span"
                onClick={() => setExpanded((v) => !v)}
                sx={{ cursor: "pointer", display: "flex", mr: 0.5, flexShrink: 0 }}
              >
                {expanded ? (
                  <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                ) : (
                  <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                )}
              </Box>
            ) : (
              <Box sx={{ width: 22, mr: 0.5, flexShrink: 0 }} />
            )}
            {row.date ? formatDate({ date: row.date }) : "—"}
          </Stack>
        </BodyCell>

        {/* Round */}
        <BodyCell>{row.round}</BodyCell>

        {/* Investor */}
        <BodyCell>
          {row.isAggregated ? (
            <Typography component="span" sx={{ fontSize: "inherit", fontStyle: "italic", color: "#71A0B2" }}>
              All investors
            </Typography>
          ) : (
            row.investor ?? "—"
          )}
        </BodyCell>

        {/* Bank Account */}
        <BodyCell>{row.bankAccount ?? "—"}</BodyCell>

        {/* Measure columns */}
        {shownMeasures.map((measure) => {
          const cell = row.cells[measure] ?? null;
          return (
            <BodyCell key={measure} sx={{ position: "relative" }}>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <span>{cell?.displayValue ?? "—"}</span>
                {cell && (
                  <IconButton
                    className="cell-pencil"
                    sx={{ p: 0.25, ml: 0.5 }}
                    onClick={() => onEditCell(row, measure)}
                  >
                    <CreateOutlinedIcon sx={{ fontSize: 14, color: "#71A0B2" }} />
                  </IconButton>
                )}
              </Stack>
            </BodyCell>
          );
        })}

        {/* Row actions */}
        <BodyCell sx={{ p: 0, width: 72 }}>
          <Stack className="row-actions" direction="row" justifyContent="flex-end">
            <IconButton onClick={() => onEditRow(row)}>
              <CreateOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton onClick={() => onDeleteRow(row)}>
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </BodyCell>
      </TableRow>

      {hasChildren &&
        expanded &&
        row.children.map((child) => (
          <WideTableRow
            key={child.key}
            row={child}
            measureColumns={measureColumns}
            visibleMeasures={visibleMeasures}
            depth={depth + 1}
            formatDate={formatDate}
            onEditRow={onEditRow}
            onEditCell={onEditCell}
            onDeleteRow={onDeleteRow}
          />
        ))}
    </>
  );
};
